import React, { useState, useEffect, useMemo } from 'react';
import { PageTransition } from '../ui/PageTransition';
import { Employee } from '../../types/employee';
import { employeeService } from '../../services/employeeService';
import { 
  EmployeesHeader,
  EmployeesList,
  AddEmployeeModal,
} from './employee-components';

const Employees: React.FC = () => {
  // Estados principais
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Estados de busca e modais
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // Carregar funcionários do Firebase
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setLoading(true);
        const employeesList = await employeeService.getEmployees();
        setEmployees(employeesList);
      } catch (err) {
        console.error('Erro ao carregar funcionários:', err);
        alert('Erro ao carregar funcionários. Tente novamente.');
      } finally {
      setLoading(false);
      }
    };

    loadEmployees();
  }, []);

  // Funcionários filtrados
  const filteredEmployees = useMemo(() => {
    if (!searchTerm) return employees;
    
    return employees.filter(employee => 
        employee.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.telefone.toLowerCase().includes(searchTerm.toLowerCase())
      );
  }, [employees, searchTerm]);

  // Funções de callback
  const handleAddEmployee = async (employeeData: Partial<Employee>) => {
    try {
      // Salvar no Firebase
      const employeeId = await employeeService.addEmployee(employeeData);
      
      // Criar objeto completo com ID do Firebase
      const newEmployee: Employee = {
        id: employeeId,
        nome: employeeData.nome!,
        telefone: employeeData.telefone!,
        status: employeeData.status!,
        dataNascimento: employeeData.dataNascimento,
      };
      
      // Atualizar estado local
      setEmployees(prev => [...prev, newEmployee]);
      setShowAddModal(false);
      
      alert('Funcionário adicionado com sucesso!');
    } catch (error) {
      console.error('Erro ao adicionar funcionário:', error);
      alert('Erro ao adicionar funcionário. Tente novamente.');
    }
  };



  // Handlers para os componentes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };



  // Funções de utilidade
  const getStatusColor = (status: string) => {
    const colors = {
      ativo: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      inativo: 'bg-red-100 text-red-700 border-red-200',
    };
    return colors[status as keyof typeof colors] || colors.ativo;
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      ativo: 'Ativo',
      inativo: 'Inativo',
    };
    return labels[status as keyof typeof labels] || 'Desconhecido';
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-slate-600">Carregando funcionários...</p>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 overflow-x-hidden">
        {/* Header Principal */}
        <EmployeesHeader
          onAddEmployee={() => setShowAddModal(true)}
        />

        {/* Layout Principal - Lista ocupa toda a largura */}
        <div className="w-full">
          <EmployeesList
            employees={filteredEmployees}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onAddEmployee={() => setShowAddModal(true)}
            getStatusColor={getStatusColor}
            getStatusLabel={getStatusLabel}
          />
        </div>
            
        {/* Modais */}
        <AddEmployeeModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddEmployee}
        />
                </div>
    </PageTransition>
  );
};

export default Employees;



